import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, Mail } from 'lucide-react';
import styles from './legal.module.css';
import livePulseLogo from '../assets/live-pulse-logo.png';

const LAST_UPDATED = 'March 12, 2026';
const CONTACT_EMAIL = 'info@ottosoft.in';
const COMPANY_NAME = 'OttoSoft';
const APP_NAME = 'LivePulse';

const SECTIONS = [
    { id: 'acceptance', num: '01', title: 'Acceptance of Terms' },
    { id: 'service', num: '02', title: 'Description of Service' },
    { id: 'accounts', num: '03', title: 'User Accounts' },
    { id: 'conduct', num: '04', title: 'Acceptable Use' },
    { id: 'content', num: '05', title: 'Content & Intellectual Property' },
    { id: 'payments', num: '06', title: 'Payments & Plans' },
    { id: 'liability', num: '07', title: 'Limitation of Liability' },
    { id: 'termination', num: '08', title: 'Termination' },
    { id: 'changes', num: '09', title: 'Changes to Terms' },
    { id: 'contact', num: '10', title: 'Contact Us' },
];

export default function TermsPage() {
    const navigate = useNavigate();

    return (
        <div className={styles.page}>
            {/* Top bar */}
            <div className={styles.topBar}>
                <div className={styles.topBarInner}>
                    <Link to="/" className={styles.logoRow}>
                        <img src={livePulseLogo} alt="LivePulse" className={styles.logoBrand} />
                    </Link>
                    <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft size={14} />
                        Back
                    </button>
                </div>
            </div>

            {/* Hero */}
            <div className={styles.hero}>
                <div className={styles.heroMesh} aria-hidden />
                <div className={styles.heroInner}>
                    <span className={styles.heroLabel}>Legal</span>
                    <h1 className={styles.heroTitle}>Terms of Service</h1>
                    <div className={styles.heroBadges}>
                        <span className={styles.heroBadge}>
                            <FileText size={12} />
                            {APP_NAME} Platform
                        </span>
                        <span className={styles.heroBadge}>
                            <Calendar size={12} />
                            Last updated: {LAST_UPDATED}
                        </span>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className={styles.body}>
                {/* Table of contents */}
                <nav className={styles.toc} aria-label="Table of contents">
                    <div className={styles.tocTitle}>Contents</div>
                    <ol className={styles.tocList}>
                        {SECTIONS.map((s) => (
                            <li key={s.id} className={styles.tocItem}>
                                <a href={`#${s.id}`}>
                                    <span className={styles.tocNum}>{s.num}</span>
                                    {s.title}
                                </a>
                            </li>
                        ))}
                    </ol>
                </nav>

                {/* Sections */}
                <section id="acceptance" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>1</span>
                        <h2 className={styles.sectionTitle}>Acceptance of Terms</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            By accessing or using {APP_NAME} (the "Service"), provided by <strong>{COMPANY_NAME}</strong>,
                            you agree to be bound by these Terms of Service ("Terms"). If you do not agree to all of
                            these Terms, you may not use the Service.
                        </p>
                        <p>
                            These Terms apply to all users of the Service, including event organizers, participants,
                            and anyone who accesses any part of the platform.
                        </p>
                        <p>
                            By creating an account or using the Service, you represent that you are at least 13 years
                            of age and have the legal capacity to enter into these Terms.
                        </p>
                    </div>
                </section>

                <section id="service" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>2</span>
                        <h2 className={styles.sectionTitle}>Description of Service</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            {APP_NAME} is a real-time audience engagement platform that allows event organizers to
                            create interactive activities — including polls, quizzes, Q&amp;A sessions, word clouds,
                            and more — and enables participants to join and respond using a 6-digit event code without
                            requiring app downloads or account creation.
                        </p>
                        <p>The Service includes the following core features:</p>
                        <ul>
                            <li>Creation and management of events and interactive activities</li>
                            <li>Real-time participant response collection and live result display</li>
                            <li>25 activity types, including Multiple Choice, Word Cloud, Open Text, Ranking, Rating, Quiz, Spinner Wheel, Correct Order, Categories, Rating Scale, and Audience Q&amp;A</li>
                            <li>AI-powered question suggestions</li>
                            <li>Export and analytics for activity results</li>
                            <li>Participant join via 6-digit code — no sign-up required for participants</li>
                        </ul>
                        <p>
                            We reserve the right to modify, suspend, or discontinue any part of the Service at any
                            time, with or without notice.
                        </p>
                    </div>
                </section>

                <section id="accounts" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>3</span>
                        <h2 className={styles.sectionTitle}>User Accounts</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            To create events and manage activities, you must register for an account. You agree to:
                        </p>
                        <ul>
                            <li>Provide accurate, current, and complete information during registration</li>
                            <li>Maintain and promptly update your account information to keep it accurate</li>
                            <li>Keep your password confidential and not share it with others</li>
                            <li>Notify us immediately of any unauthorised use of your account</li>
                            <li>Be responsible for all activities that occur under your account</li>
                        </ul>
                        <p>
                            We reserve the right to suspend or terminate accounts that violate these Terms or that
                            have been inactive for an extended period.
                        </p>
                    </div>
                </section>

                <section id="conduct" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>4</span>
                        <h2 className={styles.sectionTitle}>Acceptable Use</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>You agree not to use the Service to:</p>
                        <ul>
                            <li>Post, transmit, or share content that is unlawful, harmful, threatening, abusive, harassing, defamatory, or obscene</li>
                            <li>Violate any applicable local, national, or international law or regulation</li>
                            <li>Impersonate any person or entity or falsely claim affiliation with any person or entity</li>
                            <li>Upload or transmit viruses, malware, or any other malicious code</li>
                            <li>Attempt to gain unauthorised access to any part of the Service or its related systems</li>
                            <li>Use automated means (bots, scrapers, crawlers) to access the Service without our prior written consent</li>
                            <li>Engage in any activity that interferes with or disrupts the Service or its servers</li>
                            <li>Collect or harvest personal data of other users without their consent</li>
                        </ul>
                        <p>
                            We may use profanity filters and moderation tools to automatically block inappropriate
                            content submitted by participants. Organisers are responsible for moderating their own events.
                        </p>
                    </div>
                </section>

                <section id="content" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>5</span>
                        <h2 className={styles.sectionTitle}>Content &amp; Intellectual Property</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            <strong>Your Content:</strong> You retain ownership of any content you create using the
                            Service (questions, poll options, event names, etc.). By submitting content, you grant
                            {APP_NAME} a limited, non-exclusive, royalty-free licence to store and display it solely
                            for the purpose of operating the Service.
                        </p>
                        <p>
                            <strong>Our Content:</strong> All platform code, design, logos, trademarks, and service
                            names ("LivePulse") are the exclusive property of {COMPANY_NAME} and are protected by
                            applicable intellectual property laws. You may not copy, reproduce, distribute, or create
                            derivative works without our express written permission.
                        </p>
                        <p>
                            <strong>Participant Responses:</strong> Responses submitted by event participants are
                            associated with your event and accessible to you as the organiser. Participants submit
                            responses voluntarily and anonymously unless they choose to identify themselves.
                        </p>
                    </div>
                </section>

                <section id="payments" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>6</span>
                        <h2 className={styles.sectionTitle}>Payments &amp; Plans</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            {APP_NAME} offers paid plans (Silver, Gold, Platinum, Diamond) in addition to limited free
                            access. By purchasing a plan, you agree to:
                        </p>
                        <ul>
                            <li>Pay all applicable fees for the selected plan at the time of purchase</li>
                            <li>Provide valid payment information; all payments are processed securely</li>
                            <li>Understand that plan features are as described on our pricing page at the time of purchase</li>
                            <li>Acknowledge that fees are non-refundable except as required by applicable law</li>
                        </ul>
                        <p>
                            We reserve the right to change our pricing at any time. Changes will be communicated
                            via email and will not affect plans already purchased.
                        </p>
                    </div>
                </section>

                <section id="liability" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>7</span>
                        <h2 className={styles.sectionTitle}>Limitation of Liability</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            The Service is provided on an "as is" and "as available" basis without warranties of
                            any kind, express or implied, including but not limited to merchantability, fitness for
                            a particular purpose, or non-infringement.
                        </p>
                        <p>
                            To the fullest extent permitted by law, {COMPANY_NAME} shall not be liable for any
                            indirect, incidental, special, consequential, or punitive damages arising out of or
                            in connection with your use of the Service, including loss of data, loss of profits,
                            or service interruption.
                        </p>
                        <p>
                            Our total liability to you for any claim arising out of these Terms shall not exceed
                            the amount you paid us in the twelve (12) months preceding the claim.
                        </p>
                    </div>
                </section>

                <section id="termination" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>8</span>
                        <h2 className={styles.sectionTitle}>Termination</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            You may delete your account at any time from the Settings page. Upon deletion, your
                            events and associated data will be permanently removed from our servers within 30 days.
                        </p>
                        <p>
                            We may suspend or terminate your access to the Service immediately and without notice
                            if we determine, in our sole discretion, that you have violated these Terms or that
                            your continued use poses a risk to the Service or other users.
                        </p>
                        <p>
                            All provisions of these Terms that by their nature should survive termination shall
                            survive, including ownership provisions, warranty disclaimers, and limitations of liability.
                        </p>
                    </div>
                </section>

                <section id="changes" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>9</span>
                        <h2 className={styles.sectionTitle}>Changes to Terms</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            We reserve the right to modify these Terms at any time. When we make material changes,
                            we will update the "Last updated" date at the top of this page and notify registered
                            users by email.
                        </p>
                        <p>
                            Your continued use of the Service after any changes to the Terms constitutes your
                            acceptance of the revised Terms. If you do not agree to the new Terms, you must stop
                            using the Service.
                        </p>
                        <p>
                            These Terms are governed by the laws of India, and any disputes shall be subject to
                            the exclusive jurisdiction of the courts of Patna, Bihar.
                        </p>
                    </div>
                </section>

                <section id="contact" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>10</span>
                        <h2 className={styles.sectionTitle}>Contact Us</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            If you have any questions, concerns, or requests regarding these Terms, please reach
                            out to us:
                        </p>
                        <div className={styles.contactBox}>
                            <p><strong>{COMPANY_NAME}</strong></p>
                            <p>
                                <Mail size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                                Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                            </p>
                            <p>
                                Address: 1st Floor, Shekhar Complex Khoja Imli Mazar,
                                Phulwarisarif, Patna-801505, Bihar, India
                            </p>
                        </div>
                    </div>
                </section>
            </div>

            {/* Footer */}
            <footer className={styles.footer}>
                <div className={styles.footerInner}>
                    <span className={styles.footerText}>© {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.</span>
                    <div className={styles.footerLinks}>
                        <Link to="/terms">Terms of Service</Link>
                        <Link to="/privacy">Privacy Policy</Link>
                        <Link to="/">Home</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
